import {
  createContext,
  FC,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';

import { FullPageLoading } from '../components/loading/FullPageLoading';
import {
  AccountError,
  CurrentUserDocument,
  DefaultCurrentUserFragment,
  ViewerCompanyFragment,
} from '../graphql/generated/graphql';
import { logger } from '../initialisers/logger';
import { isManagementPath } from '../layouts/application/SectionContext';
import { clearCompanyChosen, onSessionCompanyChanged, setExpectedCompanyId } from '../lib/companySession';
import { CompanyChangeContext, useCompanySessionReset } from '../lib/hooks/useCompanySessionReset';
import { useFetchGeneric } from '../lib/hooks/useGenericFetch';
import { AccountErrorPage } from '../pages/account-error/AccountErrorPage';
import { useAuthKit } from './AuthKitProvider';
import { createFeatureFlagAuthUser } from './FeatureFlagProvider';

/** Overload the current user type to add a extra fields */
type CurrentUser = DefaultCurrentUserFragment & {
  displayName: string;
};

/**
 * Identifies a WorkOS team admin running the current request via Dashboard impersonation.
 * Mirrors the `impersonator` block on `CurrentWorkosSession` so the SPA can render the
 * impersonation banner without re-fetching anything once the bootstrap query has settled.
 */
export type Impersonator = Readonly<{
  email: string;
  reason: string;
}>;

export type WorkosSession = Readonly<{
  workosUserId: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  impersonator?: Impersonator | null;
  /**
   * Set when the verified session can't be mapped to a usable Altitude account. When present,
   * `ViewerProvider` renders the blocking contact-support page instead of the app — it must
   * NOT be treated like the `currentUser`-null self-signup state (which routes to onboarding).
   */
  accountError?: AccountError | null;
}>;

export type ViewerProviderState = Readonly<{
  user: CurrentUser | undefined;
  /**
   * The company the session operates, as the server names it (`Query.currentCompany`): the acting company while
   * acting as one. Never read off `user.company`, which is always the user's own company.
   */
  company: ViewerCompanyFragment | undefined;
  /**
   * The WorkOS session attached to the current request. Set whenever AuthKit has
   * authenticated the user, even if no Altitude account exists yet (the brand-new signup
   * state, where we route them to `/onboarding/register` to create the account).
   */
  workosSession: WorkosSession | undefined;
  /**
   * Set when the current request is being made through a WorkOS Dashboard impersonation
   * session. Drives the in-app impersonation banner and, in time, audit attribution.
   */
  impersonator: Impersonator | undefined;
  updateUser: (user: DefaultCurrentUserFragment) => void;
  logoutUser: () => void;
}>;

/** Build the current user object with the extra fields */
const buildCurrentUser = (user: DefaultCurrentUserFragment): CurrentUser => ({
  ...user,
  displayName: `${user.firstName} ${user.lastName}`,
});

export const ViewerContext = createContext<ViewerProviderState | undefined>(undefined);

export const ViewerProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<{
    user: DefaultCurrentUserFragment | undefined;
    company: ViewerCompanyFragment | undefined;
    workosSession: WorkosSession | undefined;
  }>({ user: undefined, company: undefined, workosSession: undefined });
  const [setupCompleted, setSetupCompleted] = useState<boolean>(false);
  const featureFlagAuthUser = createFeatureFlagAuthUser();

  const { data: userData, loading } = useFetchGeneric(CurrentUserDocument);

  const { signOut: authKitSignOut } = useAuthKit();

  const updateUser = useCallback(
    (userToUpdate: DefaultCurrentUserFragment) => {
      // Only onboarding hands a user over this way, and nobody acts as another company while signing up: until the
      // server names the operated company, the user's own is the one the session operates.
      const company = userInfo.company ?? userToUpdate.company;
      setUserInfo((prev) => ({
        user: userToUpdate,
        company,
        workosSession: prev.workosSession,
      }));
      featureFlagAuthUser(userToUpdate, company);
    },
    [featureFlagAuthUser, userInfo.company],
  );

  // Deliberately leaves `userInfo` alone. `authKitSignOut()` always ends in a full-page
  // navigation, so the React tree is about to be replaced wholesale and clearing the viewer
  // buys nothing. What it did buy was a window in which the route guards saw an unauthenticated
  // viewer and started rendering `/login` — whose lazy chunk the redirect then cancelled,
  // crashing the app into the error screen for the length of the sign-out round-trip.
  const logoutUser = useCallback(() => {
    // The next sign-in in this tab starts over: Root lands in management until they pick a company.
    clearCompanyChosen();
    authKitSignOut();
  }, [authKitSignOut]);

  const impersonator = userInfo.workosSession?.impersonator ?? undefined;

  const [values] = useMemo(
    () => [
      {
        user: userInfo.user ? buildCurrentUser(userInfo.user) : undefined,
        company: userInfo.company,
        workosSession: userInfo.workosSession,
        impersonator,
        updateUser,
        logoutUser,
      },
      userInfo.user,
      userInfo.workosSession,
    ],
    [userInfo.user, userInfo.company, userInfo.workosSession, updateUser, logoutUser, impersonator],
  );

  useEffect(() => {
    if (!setupCompleted && !loading) {
      const fetchedSession = userData?.currentWorkosSession ?? undefined;
      if (userData?.currentUser && userData.currentCompany) {
        logger.debug('Log user from token');
        setUserInfo({
          user: userData.currentUser,
          company: userData.currentCompany,
          workosSession: fetchedSession,
        });
        featureFlagAuthUser(userData.currentUser, userData.currentCompany);
      } else if (fetchedSession) {
        // WorkOS-authenticated but no Altitude account yet — bootstrap state for the
        // new-company self-signup flow.
        setUserInfo({
          user: undefined,
          company: undefined,
          workosSession: fetchedSession,
        });
      }
      setSetupCompleted(true);
    }
  });

  // Mirror Apollo cache changes into local state once bootstrap is done. Without this,
  // mutations that update the current user or its company (e.g. changing the company
  // currency) wouldn't be visible until a page refresh, because `userInfo` would stay
  // pinned to the snapshot taken at startup.
  useEffect(() => {
    const currentUser = userData?.currentUser;
    const currentCompany = userData?.currentCompany;
    if (!setupCompleted || !currentUser || !currentCompany) {
      return;
    }
    setUserInfo((prev) =>
      prev.user === currentUser && prev.company === currentCompany
        ? prev
        : { ...prev, user: currentUser, company: currentCompany },
    );
  }, [setupCompleted, userData?.currentUser, userData?.currentCompany]);

  // Every request names the company this page holds, so the backend can refuse one sent after the session moved.
  useEffect(() => {
    setExpectedCompanyId(userInfo.company?.id);
  }, [userInfo.company]);

  // Acting as a company changes the company the gates are evaluated for, so the flag user follows the viewer's company.
  const flagCompanyId = useRef<string | undefined>(undefined);
  useEffect(() => {
    const { user, company } = userInfo;
    if (!user || !company || flagCompanyId.current === company.id) {
      return;
    }
    const isFirstUser = flagCompanyId.current === undefined;
    flagCompanyId.current = company.id;
    if (!isFirstUser) {
      featureFlagAuthUser(user, company);
    }
  }, [userInfo, featureFlagAuthUser]);

  // While the page moves company the routed pages are swapped for a spinner, so none of their queries is live.
  const [companyChangeInProgress, setCompanyChangeInProgressState] = useState(false);
  const setCompanyChangeInProgress = useCallback((inProgress: boolean) => {
    if (inProgress) {
      // Committed now rather than on the next frame: the store reset that follows must find no page query to refetch.
      flushSync(() => setCompanyChangeInProgressState(true));
      return;
    }
    // A transition, like the router's navigation home in the same tick: both commit together, so the previous page
    // never renders again, and the spinner holds until the home page's lazy chunk has loaded.
    startTransition(() => setCompanyChangeInProgressState(false));
  }, []);

  if (setupCompleted) {
    // Terminal blocking state: a verified session that can't be mapped to a usable account.
    // Rendered here (above the router) so there's no route to slip past it into onboarding,
    // and so the bootstrap query simply resolving to this signal can never trigger a redirect
    // loop. Sign-out is the only way forward.
    if (userInfo.workosSession?.accountError) {
      return <AccountErrorPage error={userInfo.workosSession.accountError} onSignOut={logoutUser} />;
    }
    return (
      <ViewerContext.Provider value={values}>
        <CompanyChangeContext.Provider value={setCompanyChangeInProgress}>
          <SessionCompanyChangedHandler />
          {companyChangeInProgress ? <FullPageLoading /> : children}
        </CompanyChangeContext.Provider>
      </ViewerContext.Provider>
    );
  }
  return <FullPageLoading />;
};

/**
 * Reacts to the backend refusing a request because the session moved company: another tab acted as another company,
 * the act-as expired or the rule no longer allows it. Rendered beside the routed pages, not inside them, so it stays
 * mounted while they are hidden for the reset.
 */
const SessionCompanyChangedHandler: FC = () => {
  const resetForNewCompany = useCompanySessionReset();

  useEffect(
    () =>
      onSessionCompanyChanged(() => {
        // No toast: whoever moved the session already knows, and the page simply reloads onto the new company.
        // A management page holds no company data, so the operator stays on it; an app page goes home.
        resetForNewCompany(undefined, { stayOnPage: isManagementPath(window.location.pathname) }).catch(
          (error: unknown) => logger.error('Reset after a company change failed', error),
        );
      }),
    [resetForNewCompany],
  );

  return null;
};

type ViewerUse = {
  /** @deprecated Use `currentUser` instead */
  user: CurrentUser | undefined;
  currentUser: CurrentUser | undefined;
  /** Available even when `currentUser` is undefined (the brand-new signup state). */
  workosSession: WorkosSession | undefined;
  /** Set when the request is going through a WorkOS Dashboard impersonation session. */
  impersonator: Impersonator | undefined;
  updateUser: (user: DefaultCurrentUserFragment) => void;
  isAuthenticated: () => boolean;
  /** The company the session operates (the acting company while acting as one); undefined when signed out. */
  currentCompany: ViewerCompanyFragment | undefined;
};

const sharedContextHelpers = (context: ViewerProviderState): ViewerUse => ({
  user: context.user,
  currentUser: context.user,
  workosSession: context.workosSession,
  impersonator: context.impersonator,
  updateUser: context.updateUser,
  isAuthenticated: () => !!context.user,
  currentCompany: context.company,
});

/**
 * This can be used in the application when you are not sure the user is present
 */
export const useViewer = (): ViewerUse => {
  const context = useContext(ViewerContext);

  if (context === undefined) {
    throw new Error(`useViewer must be used within an ViewerProvider`);
  }

  return {
    ...sharedContextHelpers(context),
  };
};

/**
 * This is the current company object that is used in the application and provided with the current user
 */
export type CurrentCompany = {
  features?: {
    whatsapp?: boolean;
  };
  currency: string;
  bookingFee: number;
  domesticCcSurchargePercentage?: number;
  internationalCcSurchargePercentage?: number;
  /** The uploaded company logo, replacing the initial tile in the sidebar when set. */
  logo?: {
    /** Row id of the logo file. */
    id: string;
    /** Public link the sidebar renders. */
    url?: string;
  };
  configPolicies: {
    flight: {
      shortDurationHours: number;
      longDurationHours: number;
    };
    accommodation: {
      shortDurationDays: number;
      longDurationDays: number;
    };
    vehicle: {
      shortDurationDays: number;
      longDurationDays: number;
    };
  };
  /** Company tax jurisdiction — drives the GST row on PriceSummary / BasketPrice. */
  taxConfig?: {
    jurisdiction: string;
    rate: number;
    label: string;
  };
};

type AuthenticatedViewerUse = ViewerUse & {
  /** @deprecated Use `currentUser` instead */
  user: DefaultCurrentUserFragment;
  currentUser: DefaultCurrentUserFragment;
  /** The company the session operates: the acting company while acting as one. `currentUser.company` is their own. */
  currentCompany: ViewerCompanyFragment;
  logoutUser: () => void;
};

/**
 * This can be used in the application when you are sure the user is present
 */
export const useAuthenticatedViewer = (): AuthenticatedViewerUse => {
  const context = useContext(ViewerContext);

  if (context === undefined) {
    throw new Error(`useAuthViewer must be used within an ViewerProvider`);
  }

  if (!context.user || !context.company) {
    throw new Error(`useAuthViewer must be used with an authenticated user`);
  }

  return {
    ...sharedContextHelpers(context),
    user: context.user,
    currentUser: context.user,
    currentCompany: context.company,
    logoutUser: context.logoutUser,
  };
};
